import { BUSINESS, MEDIA } from "@/lib/constants";
import { ArrowRight, Phone, ShieldCheck, Sparkles } from "lucide-react";

export default function Hero({ onQuoteClick }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={MEDIA.heroBg}
          srcSet={`${MEDIA.heroBg}&w=800 800w, ${MEDIA.heroBg}&w=1600 1600w, ${MEDIA.heroBg}&w=2200 2200w`}
          sizes="100vw"
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="2200"
          height="1200"
          className="h-full w-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-[#0a1210]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(115,212,74,0.16),transparent_55%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 pt-36 lg:pt-44 pb-24 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <div className="ppc-reveal ppc-d-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ppc-glass">
              <Sparkles size={14} className="text-[#8AB273]" />
              <span className="label-eyebrow text-[#8AB273]">
                Niagara&apos;s year-round property crew
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display ppc-reveal ppc-d-2 mt-8 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-extrabold leading-[0.95] tracking-tighter"
            >
              A property that
              <br />
              looks <span className="text-[#8AB273]">cared for</span>.
              <br />
              <span className="text-white/60">All year long.</span>
            </h1>

            <p className="ppc-reveal ppc-d-3 mt-8 text-lg lg:text-xl text-white/75 max-w-2xl leading-relaxed">
              From spring mow to winter plow — Premium Property Care keeps homes
              and businesses across the {BUSINESS.region} sharp, safe, and
              effortless to own.
            </p>

            <div className="ppc-reveal ppc-d-4 mt-10 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-quote-btn"
                onClick={onQuoteClick}
                className="group inline-flex items-center justify-center gap-2 bg-[#8AB273] text-black font-semibold px-6 py-4 rounded-full hover:bg-[#9DC585] transition-all"
              >
                Get my free quote
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
              <a
                href={BUSINESS.phoneHref}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 ppc-glass px-6 py-4 rounded-full text-white font-medium hover:bg-white/5 transition"
              >
                <Phone size={16} className="text-[#8AB273]" />
                Call {BUSINESS.phone}
              </a>
            </div>

            <div className="ppc-reveal ppc-d-5 mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-white/55">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={15} className="text-[#8AB273]" /> Fully
                insured
              </span>
              <span>Same-day junk removal</span>
              <span>Locally owned</span>
              <span>Residential + Commercial</span>
              <span>Free no-obligation quotes</span>
            </div>
          </div>

          {/* Pricing badge card */}
          <div className="lg:col-span-4 ppc-reveal ppc-d-4">
            <div className="ppc-glass ppc-glow rounded-3xl p-7 lg:p-8">
              <div className="label-eyebrow">Monthly Packages Starting At</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-6xl lg:text-7xl font-extrabold tracking-tighter text-white">
                  ${BUSINESS.startingPrice}
                </span>
                <span className="text-white/50 text-sm">+ tax</span>
              </div>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">
                Our most popular plan covers regular mowing, edging, trim &
                clean-up — booked on a recurring schedule that fits your
                property.
              </p>
              <div className="my-6 h-px w-full bg-white/10" />
              <ul className="space-y-2 text-sm text-white/75">
                <li className="flex gap-2">
                  <span className="text-[#8AB273]">✓</span>Recurring mow + edge
                  + blow
                </li>
                <li className="flex gap-2">
                  <span className="text-[#8AB273]">✓</span>Seasonal touch-ups
                </li>
                <li className="flex gap-2">
                  <span className="text-[#8AB273]">✓</span>Same-crew continuity
                </li>
                <li className="flex gap-2">
                  <span className="text-[#8AB273]">✓</span>Priority booking for
                  add-ons
                </li>
              </ul>
              <button
                data-testid="hero-pricing-cta"
                onClick={onQuoteClick}
                type="button"
                className="mt-7 w-full bg-white text-black font-semibold py-3 rounded-full hover:bg-white/90 transition"
              >
                Lock in this rate
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
